import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { RequalificationRefusedNextStep } from '@/api';
import Button from '@/design_system/Button';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import IconCancel from '@/icons/Cancel.svg';
import { useRefuseRequalification, useValidateRequalification } from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';

import { ArticleActionProps } from './ArticleActions';
import { QuoteRefusalDialog } from './QuoteRefusalDialog';

export const AcceptRequalificationAction = ({ onActionDone }: ArticleActionProps) => {
  const { t } = useLingui();

  const { view: requestView } = useRequestContext();
  const { article, state, hasError, view } = useArticleContext();

  const [apiError, setApiError] = useState<string | null>(null);

  const {
    mutateAsync: refuseRequalification,
    isPending: isPendingRefuseRequalification,
    isSuccess: isSuccessRefuseRequalification,
  } = useRefuseRequalification({
    articleId: article.id,
  });

  const {
    mutateAsync: validateRequalification,
    isPending: isPendingValidateRequalification,
    isSuccess: isSuccessValidateRequalification,
  } = useValidateRequalification({
    articleId: article.id,
  });

  const [isOpenRefusalModal, setIsOpenRefusalModal] = useState(false);

  const handleRefuseRequalification = async (
    reason: string,
    nextStep: RequalificationRefusedNextStep
  ) => {
    await refuseRequalification({ comment: reason, nextStep });
    setIsOpenRefusalModal(false);
    onActionDone();
  };

  const handleValidateRequalification = async () => {
    const { hasError } = state.errors.check({
      services: {
        actions: view.services.actions.shown
          ? {
              actions: {
                missingDescription: view.services.actions.fields.description.editable,
                missingOrganizationPrice:
                  requestView.organizationPrice.shown &&
                  view.services.actions.fields.organizationPrice.editable &&
                  view.services.actions.fields.organizationPrice.required,
                organizationPriceUpdateInProgress:
                  requestView.organizationPrice.shown &&
                  view.services.actions.fields.organizationPrice.editable,
                spareParts: view.services.actions.fields.spareParts.shown
                  ? {
                      missingOrganizationPrice:
                        requestView.organizationPrice.shown &&
                        view.services.actions.fields.spareParts.organizationPrice.editable &&
                        view.services.actions.fields.spareParts.organizationPrice.required,
                      organizationPriceUpdateInProgress:
                        requestView.organizationPrice.shown &&
                        view.services.actions.fields.spareParts.organizationPrice.editable,
                    }
                  : undefined,
              },
            }
          : undefined,
        exchange: view.services.exchange.shown
          ? {
              missingOrganizationPrice:
                requestView.organizationPrice.shown &&
                view.services.exchange.organizationPrice.editable &&
                view.services.exchange.organizationPrice.required,
              organizationPriceUpdateInProgress:
                requestView.organizationPrice.shown &&
                view.services.exchange.organizationPrice.editable,
            }
          : undefined,
      },
    });

    setApiError(null);

    if (!hasError) {
      try {
        await validateRequalification();
        onActionDone();
      } catch (err: any) {
        console.error(err);
        setApiError(
          (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
        );
      }
    }
  };

  return (
    <>
      <Footer>
        {(hasError || apiError) && (
          <FooterError>
            {hasError ? (
              <Trans id="article.actions.accept_requalification.errors">
                Please fill the missing information in order to validate or refuse the
                requalification
              </Trans>
            ) : (
              apiError
            )}
          </FooterError>
        )}
        <FooterButtons>
          <Button
            variant="secondary"
            size="medium"
            onPress={() => setIsOpenRefusalModal(true)}
            isLoading={isPendingRefuseRequalification || isSuccessRefuseRequalification}
            className="text-no-wrap"
          >
            <IconCancel />
            <Trans id="article.actions.accept_requalification.refuse">Refuse quote</Trans>
          </Button>
          <Button
            variant="primary"
            size="medium"
            onPress={handleValidateRequalification}
            isLoading={isPendingValidateRequalification || isSuccessValidateRequalification}
            className="text-no-wrap"
          >
            <Trans id="article.actions.accept_requalification.validate">Validate quote</Trans>
          </Button>
        </FooterButtons>
      </Footer>
      <QuoteRefusalDialog
        isOpen={isOpenRefusalModal}
        onClose={() => setIsOpenRefusalModal(false)}
        onRefuse={handleRefuseRequalification}
        isLoading={isPendingRefuseRequalification || isSuccessRefuseRequalification}
      />
    </>
  );
};
